import type { RuntimeConfig } from './config'

type Capture = (error: unknown) => void

const DROPPED_INTEGRATIONS = new Set(['Console', 'BrowserSession'])

export function withoutQuery(url: string): string {
  return url.split(/[?#]/, 1)[0] ?? url
}

let enabled = false
let capture: Capture | null = null
// errors raised while the SDK chunk is still loading
const pending: unknown[] = []

/**
 * Sends errors to GlitchTip (Sentry protocol) only when the container sets a DSN.
 * The SDK is a separate chunk: without a DSN it is never downloaded.
 * No personal data: user, cookies, headers, bodies, query strings, console and sessions are off; no tracing.
 */
export async function startMonitoring(config: RuntimeConfig): Promise<boolean> {
  if (!config.sentryDsn) return false
  enabled = true
  const Sentry = await import('@sentry/react')
  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.environment,
    release: config.version,
    // SDK 11 collects everything by default; turn each category off (LGPD)
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      stackFrameVariables: false,
    },
    tracesSampleRate: 0,
    // console messages can carry API data; BrowserSession would send one envelope (with the
    // student's IP) per visit even without errors
    integrations: (defaults) =>
      defaults.filter((integration) => !DROPPED_INTEGRATIONS.has(integration.name)),
    // the SDK does not gate URLs by dataCollection: strip query strings ourselves
    beforeSend: (event) => {
      if (event.request?.url) event.request.url = withoutQuery(event.request.url)
      return event
    },
    beforeBreadcrumb: (breadcrumb) => {
      const data = breadcrumb.data
      if (data) {
        for (const key of ['url', 'from', 'to']) {
          if (typeof data[key] === 'string') data[key] = withoutQuery(data[key])
        }
      }
      return breadcrumb
    },
  })
  capture = (error) => Sentry.captureException(error)
  pending.splice(0).forEach(capture)
  return true
}

/** Reports an error caught by the app (route error boundary, etc.). No-op without a DSN. */
export function reportError(error: unknown): void {
  if (!enabled) return
  if (capture) capture(error)
  else pending.push(error)
}
